/**
 * This is the client-side entrypoint for your tRPC API. It is used to create the `api` object which
 * contains the Next.js App-wrapper, as well as your type-safe React Query hooks.
 *
 * We also create a few inference helpers for input and output types.
 */
import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server';
import type { NextPageContext } from 'next';
import { createWSClient, httpLink, loggerLink, splitLink, wsLink } from '@trpc/client';
import type { CreateTRPCNext } from '@trpc/next';
import { createTRPCNext } from '@trpc/next';
import { ssrPrepass } from '@trpc/next/ssrPrepass';
import { MutationCache, QueryCache } from '@tanstack/react-query';
import { getAuth } from 'firebase/auth';
import superjson from 'superjson';

import type { AppRouter } from '@poseidon/api/types';

import { actingImpersonationToken, expireImpersonationSession, impersonationSessionGet } from '~/auth/auth.context';
import { env } from '~/env';

// When the server rejects a stale impersonation token (message 'IMPERSONATION_EXPIRED'), end the
// session: drop it and reload, so the next requests run as the real admin.
function handleImpersonationExpired(error: unknown): void {
  const message = error instanceof Error ? error.message : undefined;
  if (message === 'IMPERSONATION_EXPIRED') expireImpersonationSession(impersonationSessionGet()?.token);
}

export function getBaseUrl() {
  if (typeof window !== 'undefined') return env.NEXT_PUBLIC_BACKEND_URL;
  // SSR: prefer internal Cloud Run URL for lower latency, fall back to public URL
  return env.NEXT_PUBLIC_BACKEND_URL;
}

function getLastLink() {
  if (typeof window === 'undefined') {
    return getHttpLink();
  }

  return splitLink({
    condition: (op) => op.type === 'subscription',
    true: getWSLink(),
    false: getHttpLink(),
  });
}

/**
 * Custom fetch wrapper that handles HTML error responses gracefully
 * Prevents "Unexpected token '<', "<!DOCTYPE "... is not valid JSON" errors
 * 
 * TRPC expects JSON responses, but sometimes the server returns HTML error pages.
 * This wrapper intercepts those and converts them to proper JSON error responses.
 */
async function safeFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const response = await fetch(input, init);
  
  // Check content-type header first
  const contentType = response.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');
  const isHtml = contentType.includes('text/html');
  
  // If content-type explicitly says JSON and response is OK, trust it
  if (isJson && response.ok && !isHtml) {
    return response;
  }
  
  // Otherwise, we need to peek at the body to check if it's HTML
  // Clone the response so we can read it without consuming the original
  const clonedResponse = response.clone();
  
  try {
    const text = await clonedResponse.text();
    const trimmedText = text.trim();
    
    // Check if response body is HTML (even if content-type says otherwise)
    const looksLikeHtml =
      trimmedText.startsWith('<!DOCTYPE') ||
      trimmedText.startsWith('<!doctype') ||
      trimmedText.startsWith('<html') ||
      trimmedText.startsWith('<HTML') ||
      trimmedText.startsWith('<?xml') ||
      (trimmedText.startsWith('<') && 
       !trimmedText.startsWith('{') && 
       !trimmedText.startsWith('[') &&
       !trimmedText.startsWith('"'));
    
    if (looksLikeHtml) {
      // This is HTML, not JSON - convert to proper TRPC error format
      const errorStatus = response.ok ? 500 : response.status;
      
      // Create a new response with JSON error body
      // We need to recreate the response because we consumed the cloned one
      return new Response(
        JSON.stringify({
          error: {
            message: `Server returned HTML instead of JSON (${response.status} ${response.statusText}). This usually indicates a server error, authentication issue, or routing problem.`,
            code: 'INTERNAL_SERVER_ERROR',
            data: {
              code: 'INTERNAL_SERVER_ERROR',
              httpStatus: errorStatus,
              originalStatus: response.status,
            },
          },
        }),
        {
          status: errorStatus,
          statusText: response.statusText || 'Internal Server Error',
          headers: {
            'content-type': 'application/json',
          },
        },
      );
    }
    
    // Body is not HTML - return original response
    // Note: The original response body is still intact because we cloned it
    return response;
  } catch (e) {
    // If we can't read the response body, return original
    // This shouldn't happen, but better safe than sorry
    console.warn('Failed to read response body in safeFetch:', e);
    return response;
  }
}

function getHttpLink() {
  return httpLink({
    url: `${getBaseUrl()}/api/trpc`,
    // maxURLLength: 2000,
    fetch: safeFetch,
    headers: async ({ op }) => {
      // Wait for Firebase to settle the initial auth state before issuing the
      // request. Without this, queries that mount before Firebase has restored
      // the persisted session send no Authorization header, get a 401, and only
      // recover on React Query's ~1s retry backoff — a visible load-time stall.
      // Client-only: server-side (SSR) has no persisted session to restore.
      if (typeof window !== 'undefined') {
        await getAuth().authStateReady();
      }
      const authToken = await getAuth().currentUser?.getIdToken();
      // On superadmin pages we act as the superadmin, not the impersonated user.
      let impersonationToken = actingImpersonationToken();
      // Starting an impersonation must always run as the superadmin: sent under another
      // user's token, the server would record THAT user as the impersonator. Dropping it
      // here lets the superadmin bar switch people in one call (no stop / wait / restart).
      if (op.path === 'superAdmin.impersonate') {
        impersonationToken = undefined;
      }

      const headers: Record<string, string> = {};

      if (authToken) {
        headers.authorization = `Bearer ${authToken}`;
      }

      if (impersonationToken) {
        headers['x-impersonation-token'] = impersonationToken;
      }

      // Identify this surface for the audit log (server reads x-poseidon-client).
      headers['x-poseidon-client'] = 'dashboard';
      headers['x-poseidon-client-version'] = env.NEXT_PUBLIC_APP_VERSION;

      return headers;
    },
    transformer: superjson,
  });
  // return httpBatchLink({
  //   url: `${getBaseUrl()}/api/trpc`,
  //   headers: async () => {
  //     const authToken = await getAuth().currentUser?.getIdToken();
  //     if (!authToken) return {};
  //     return {
  //       authorization: `Bearer ${authToken}`,
  //     };
  //   },
  //   transformer: superjson,
  // });
}

let wsClient: ReturnType<typeof createWSClient> | undefined;
function getWSLink() {
  if (wsClient == null) {
    const apiUrl = new URL(env.NEXT_PUBLIC_BACKEND_URL);
    const protocol = apiUrl.protocol === 'https:' ? 'wss' : 'ws';

    wsClient = createWSClient({
      url: `${protocol}://${apiUrl.host}/api/ws`,
      lazy: { enabled: true, closeMs: 0 },
      onClose(cause) {
        console.log('ws closed:', cause);
      },
      onOpen() {
        console.log('ws opened');
      },
    });
  }

  return wsLink<AppRouter>({
    client: wsClient,
    /**
     * Transformer used for data de-serialization from the server.
     *
     * @see https://trpc.io/docs/data-transformers
     */
    transformer: superjson,
  });
}

/** A set of type-safe react-query hooks for your tRPC API. */
export const api: CreateTRPCNext<AppRouter, NextPageContext> = createTRPCNext<AppRouter>({
  config: () => {
    return {
      queryClientConfig: {
        queryCache: new QueryCache({ onError: handleImpersonationExpired }),
        mutationCache: new MutationCache({ onError: handleImpersonationExpired }),
        defaultOptions: {
          queries: {
            // added this flag for development purposes, to avoid refetching on window focus
            // DEFAULT behaviour - (if this is not set), the page will refetch on window focus
            refetchOnWindowFocus: env.NEXT_PUBLIC_DISABLE_REFETCH_ON_WINDOW_FOCUS !== true,
            // Prevent unnecessary refetches - data is considered fresh for 30 seconds
            // This helps with performance when components re-mount or tabs switch
            staleTime: 30 * 1000,
          },
        },
      },

      /**
       * Links used to determine request flow from client to server.
       *
       * @see https://trpc.io/docs/links
       */
      links: [
        loggerLink({
          enabled: (opts) =>
            // eslint-disable-next-line no-restricted-properties
            process.env.NODE_ENV === 'development' || (opts.direction === 'down' && opts.result instanceof Error),
        }),

        getLastLink(),
      ],
    };
  },
  /**
   * Whether tRPC should await queries when server rendering pages.
   *
   * @see https://trpc.io/docs/nextjs#ssr-boolean-default-false
   */
  ssr: true,
  ssrPrepass: ssrPrepass,
  transformer: superjson,
});

/**
 * Inference helper for inputs.
 *
 * @example type HelloInput = RouterInputs['example']['hello']
 */
export type RouterInputs = inferRouterInputs<AppRouter>;

/**
 * Inference helper for outputs.
 *
 * @example type HelloOutput = RouterOutputs['example']['hello']
 */
export type RouterOutputs = inferRouterOutputs<AppRouter>;
